/**
 * AI 보고서 보관함
 * ---------------------------------------------------------------------------
 * ## 왜 필요한가
 * 지금까지 보고서는 **만들고 버렸다.** 팝업을 닫으면 사라져서, 다시 보려면
 * 다시 만들어야 했다. 그런데 보고서는 **건당 요금**이 붙고, AI 라 같은
 * 기간으로 눌러도 글이 달라진다 — "아까 본 그 보고서" 를 되찾을 방법이
 * 아예 없었다.
 *
 * 이제 만든 것이 모두 여기 남는다. 다시 보는 데는 돈이 들지 않는다.
 *
 * ## 단가를 함께 적는다
 * 청구서에 「AI 보고서 3건」이 찍혔을 때 어느 것인지 맞춰 볼 수 있어야
 * 한다. 그때의 단가를 행에 박아 두었으므로 요금제가 바뀌어도 어긋나지
 * 않는다.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App as AntApp,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { ECO_PERMISSIONS } from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { can, useSession } from '../lib/session';
import { AiQuotaExample, AiReportButton } from '../components/AiReportDialog';
import { ReportDocument } from '../components/ReportDocument';
import { ReportCharts, type TrendSeries } from '../components/ReportCharts';
import { downloadReportPdf, safeFileName } from '../lib/reportPdf';
import { HelpH, HelpP, PageHelp } from '../components/PageHelp';

const { Text, Paragraph } = Typography;

interface AiReportSummary {
  aiReportId: string;
  siteId: string | null;
  siteName: string | null;
  periodStart: string;
  periodEnd: string;
  logCount: number;
  providerName: string | null;
  pricePerReport: number | null;
  createdAt: string;
  preview: string;
  /** 만들 때 외부 모델 제공에 동의했는가 */
  consented: boolean;
  /** 그때의 동의 문구. 문구가 바뀌어도 과거는 그대로 설명된다 */
  consentText: string | null;
}

interface AiReportDetail extends AiReportSummary {
  body: string;
  /** 그때 계산해 함께 저장한 차트용 수치. 다시 계산하지 않는다 */
  series: TrendSeries[];
}

const won = (n: number | null) =>
  n === null ? '-' : `${Math.round(n).toLocaleString('ko-KR')}원`;

export function AiReportListPage() {
  const { message, modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const canWrite = can(session, ECO_PERMISSIONS.DAILY_LOG_WRITE);

  const [openId, setOpenId] = useState<string | null>(null);
  /* PDF 로 찍을 범위 — 본문과 차트를 감싼 상자를 가리킨다 */
  const bodyRef = useRef<HTMLDivElement>(null);
  const [savingPdf, setSavingPdf] = useState(false);

  const list = useQuery<AiReportSummary[]>({
    queryKey: ['ai-reports'],
    queryFn: async () => (await api.get<AiReportSummary[]>('/ai-reports')).data,
  });

  const detail = useQuery<AiReportDetail>({
    queryKey: ['ai-reports', openId],
    queryFn: async () => (await api.get<AiReportDetail>(`/ai-reports/${openId}`)).data,
    enabled: Boolean(openId),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/ai-reports/${id}`),
    onSuccess: async () => {
      message.success('보고서를 지웠습니다.');
      setOpenId(null);
      await queryClient.invalidateQueries({ queryKey: ['ai-reports'] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });

  const rows = list.data ?? [];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PageHelp title="AI 보고서 — 화면 설명">
        <HelpP>
          만드신 AI 주간 보고서가 모두 여기 남습니다. <b>다시 보는 데는 요금이 들지
          않습니다.</b> 새로 만들 때만 건당 요금이 붙습니다.
        </HelpP>
        <HelpH>같은 기간인데 글이 다릅니다</HelpH>
        <HelpP>
          AI 가 쓰는 글이라 만들 때마다 표현이 달라집니다. 그래서 만든 것을 남겨 둡니다 —
          보고에 쓰신 그 글을 그대로 다시 꺼내실 수 있습니다.
        </HelpP>
        <HelpH>매월 무료 수량이 있고, 넘긴 만큼만 청구됩니다</HelpH>
        <HelpP>
          [AI 주간 보고서] 를 누르면 창 위에 <AiQuotaExample /> 가 떠서 이번 달에 몇 건을
          만들었는지 보여 줍니다. 괄호 안의 무료 수량은 지금{' '}
          <b>계약하신 요금제에서 읽어 온 값</b>이라 기업마다 다릅니다. 무료 수량은{' '}
          <b>기업 단위</b>(사람 수와 무관)이고{' '}
          <b>매월 1일에 새로 시작</b>하며, 쓰지 않은 수량은{' '}
          <b>다음 달로 넘어가지 않습니다.</b> 무료 수량을 넘기면{' '}
          <b>건당 비용이 발생</b>하고, 단가는 계약하신 요금제의 금액입니다 — 이 숫자는
          WORK&amp;Hub 가 세는 값을 그대로 받아 오므로 청구서와 어긋나지 않습니다.
        </HelpP>
        <HelpH>만들기 전에 동의가 필요합니다</HelpH>
        <HelpP>
          보고서를 만들면 그 기간의 운영일지 내용이 <b>선택된 AI 모델로 전달</b>됩니다.
          그래서 만들 때마다 동의 체크를 받고, <b>동의한 사실과 그때의 문구를 보고서마다
          기록</b>합니다. 체크하지 않으면 만들기 단추가 눌리지 않고, 서버도 거절합니다.
          기록은 보고서에 함께 보관되며, 필요하면 공급사가 꺼내 확인할 수 있습니다.
        </HelpP>
        <HelpH>청구서와 맞춰 보기</HelpH>
        <HelpP>
          목록에는 금액을 적지 않습니다 — 무료분으로 만든 것과 초과분으로 만든 것이
          섞여 있어, 목록을 세로로 더하면 청구서와 맞지 않기 때문입니다. 보고서를
          열어 보시면 <b>그때의 단가</b>가 적혀 있고, 청구서의 「AI 보고서 생성」 줄과는{' '}
          <b>만든 날짜</b>로 맞춰 보실 수 있습니다.
        </HelpP>
      </PageHelp>

      <Card
        size="small"
        title="AI 보고서"
        loading={list.isLoading}
        extra={<AiReportButton />}
      >
        {rows.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                아직 만드신 보고서가 없습니다.
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  오른쪽 위 [AI 주간 보고서] 로 만들어 보세요.
                </Text>
              </span>
            }
          />
        ) : (
          <Table
            size="small"
            rowKey="aiReportId"
            dataSource={rows}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            onRow={(r) => ({
              style: { cursor: 'pointer' },
              onClick: () => setOpenId(r.aiReportId),
            })}
            columns={[
              {
                title: '기간',
                width: 200,
                render: (_: unknown, r: AiReportSummary) => (
                  <span>
                    {r.periodStart} ~ {r.periodEnd}
                    {r.logCount === 0 && (
                      <Tag color="default" style={{ marginLeft: 6 }}>
                        일지 없음
                      </Tag>
                    )}
                  </span>
                ),
              },
              {
                title: '사업장',
                dataIndex: 'siteName',
                width: 150,
                render: (v: string | null) => v ?? '전체',
              },
              {
                title: '내용',
                dataIndex: 'preview',
                ellipsis: true,
                render: (v: string) => (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {v || '(내용 없음)'}
                  </Text>
                ),
              },
              { title: '일지', dataIndex: 'logCount', width: 70, align: 'right' },
              /*
               * 목록에는 요금을 적지 않는다.
               *
               * 무료 수량이 생기면서 이 칸이 사실과 달라졌다 — 무료분으로
               * 만든 보고서에도 「350원」이 찍혀, 목록을 세로로 더하면
               * 청구서보다 많이 나온다. 그때의 단가는 상세 화면에 그대로
               * 남아 있고(청구서와 맞춰 보는 자리는 거기다), 지금 얼마가
               * 나가는지는 만들기 창의 무료 잔량 줄이 말해 준다.
               */
              {
                title: '만든 날짜',
                dataIndex: 'createdAt',
                width: 170,
                render: (v: string) => new Date(v).toLocaleString('ko-KR'),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        open={Boolean(openId)}
        onCancel={() => setOpenId(null)}
        width={820}
        title={
          <Space>
            <FileTextOutlined />
            AI 주간 보고서
          </Space>
        }
        footer={[
          <Button
            key="pdf"
            icon={<DownloadOutlined />}
            loading={savingPdf}
            disabled={!detail.data}
            onClick={async () => {
              if (!bodyRef.current || !detail.data) return;
              setSavingPdf(true);
              try {
                await downloadReportPdf({
                  source: bodyRef.current,
                  fileName: safeFileName(
                    `AI보고서_${detail.data.siteName ?? '전체'}_${detail.data.periodStart}`,
                  ),
                });
              } catch (e) {
                message.error(`PDF 를 만들지 못했습니다: ${(e as Error).message}`);
              } finally {
                setSavingPdf(false);
              }
            }}
          >
            PDF 다운로드
          </Button>,
          canWrite && openId ? (
            <Button
              key="del"
              danger
              icon={<DeleteOutlined />}
              loading={remove.isPending}
              onClick={() =>
                modal.confirm({
                  title: '이 보고서를 지울까요?',
                  content: '지우면 되돌릴 수 없습니다. 다시 만들려면 요금이 또 붙습니다.',
                  okText: '지우기',
                  okButtonProps: { danger: true },
                  cancelText: '그만두기',
                  onOk: () => remove.mutateAsync(openId).then(() => undefined),
                })
              }
            >
              지우기
            </Button>
          ) : null,
          <Button key="close" onClick={() => setOpenId(null)}>
            닫기
          </Button>,
        ]}
      >
        {detail.isLoading ? (
          <Text type="secondary">불러오는 중…</Text>
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="기간">
                {detail.data.periodStart} ~ {detail.data.periodEnd}
              </Descriptions.Item>
              <Descriptions.Item label="사업장">
                {detail.data.siteName ?? '전체'}
              </Descriptions.Item>
              <Descriptions.Item label="요약에 쓰인 일지">
                {detail.data.logCount}건
              </Descriptions.Item>
              {/* 엔진·금액은 보고서 화면에 적지 않는다 — 청구서에서 본다. */}
              {/*
                동의 기록은 화면에 띄우지 않는다.

                만들 때 체크를 받았으니 **읽는 사람에게는 이미 지난 일**이고,
                보고서 머리에 동의 문구가 통째로 붙어 있으면 정작 봐야 할
                기간·사업장·일지 수가 묻힌다. 기록은 그대로 남는다
                (eco.ai_report 의 consented·consent_text) — 나중에 "무엇에
                동의하고 보냈나" 를 물으면 거기서 꺼내 보인다.
              */}
            </Descriptions>
            {/*
              본문은 마크다운이다 — 제목·굵게·표를 **문서로** 그린다.
              전에는 글자 그대로 흘려서 표가 `| 날짜 | 가동시간 |` 작대기로
              보였다. 받아서 결재에 붙이는 글이라 문서로 읽혀야 한다.
            */}
            {/* ref 는 PDF 로 찍을 범위 — 본문과 차트가 함께 들어가야 한다 */}
            <div
              ref={bodyRef}
              style={{
                maxHeight: '56vh',
                overflow: 'auto',
                background: '#fff',
                border: '1px solid #f0f0f0',
                padding: '20px 24px',
                borderRadius: 6,
              }}
            >
              <ReportDocument body={detail.data.body} />
              {detail.data.series?.length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      margin: '0 0 12px',
                      paddingLeft: 9,
                      borderLeft: '4px solid #1677ff',
                    }}
                  >
                    수치 변화
                  </div>
                  <ReportCharts series={detail.data.series} />
                </div>
              )}
            </div>
          </>
        ) : (
          <Text type="danger">보고서를 불러오지 못했습니다.</Text>
        )}
      </Modal>
    </Space>
  );
}
